import { useState, useCallback } from "react";
import { useAppSelector } from "../../store/hooks";
import { useGetQuery } from "../../store/slices/apiSlice";
import { Wellbore } from "../../store/types";

interface WellboresProps {
  wellUid?: string | null;
  uid?: string | null;
  fetchAll: boolean;
}

export const useWellboresAPI = ({
  wellUid,
  uid,
  fetchAll = false,
}: WellboresProps) => {
  const wellsEP = useAppSelector((state) => state.endpoints.wells);
  const wellboresEP = useAppSelector((state) => state.endpoints.wellbores);

  const [targetWellUid, setTargetWellUid] = useState<string | null>(null);
  const endpoint = targetWellUid
    ? `${wellsEP}/${targetWellUid}${wellboresEP}`
    : "";

  const { data, isLoading, refetch } = useGetQuery(endpoint, {
    skip: !targetWellUid,
  });

  const fetchWellbores = useCallback((wellUid: string) => {
    setTargetWellUid(wellUid);
  }, []);

  const clearLazy = useCallback(() => {
    setTargetWellUid(null);
  }, []);

  const {
    data: wellbore,
    refetch: refetchWellbore,
    isLoading: isWellboreLoading,
  } = useGetQuery(`${wellsEP}/${wellUid}${wellboresEP}/${uid}`, {
    refetchOnMountOrArgChange: true,
    skip: !wellboresEP || !wellUid || !uid,
  });

  const {
    data: allWellbores,
    refetch: refetchAllWellbores,
    isLoading: isFetchLoading,
  } = useGetQuery(wellboresEP, {
    refetchOnMountOrArgChange: true,
    skip: !wellboresEP || !fetchAll,
  });

  return {
    wellboresEP,
    // lazy
    fetchWellbores,
    wellbores: data as Wellbore[] | undefined,
    isLoading,
    refetchLazy: refetch,
    clearLazy,
    // by id
    wellbore,
    refetchWellbore,
    isWellboreLoading,
    // all
    allWellbores: allWellbores as Wellbore[] | undefined,
    refetchAllWellbores,
    isFetchLoading,
  };
};
